import { useState } from 'react'
import { RiArrowRightLine } from 'react-icons/ri'
import { useInView } from '@/hooks'
import { subscribeToMailchimp } from '@/utils/mailchimp'

const perks = [
  'Resources & birth support tips',
  'New service announcements',
  'Exclusive community offers',
]

export const NewsletterSection = () => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const { ref, inView } = useInView<HTMLDivElement>()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const res = await subscribeToMailchimp(email, name)
      if (res.result === 'success') {
        setSubmitted(true)
      } else {
        const clean = res.msg.replace(/^[0-9]+ - /, '').replace(/<[^>]+>/g, '')
        setError(clean || 'Something went wrong. Please try again.')
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className="relative bg-crimson-900 py-20 lg:py-24 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute top-0 right-0 w-80 h-80 rounded-full bg-crimson-700 opacity-20 -translate-y-1/3 translate-x-1/3"
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-crimson-700 opacity-15 translate-y-1/3 -translate-x-1/4"
      />
      <div
        aria-hidden="true"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white opacity-[0.03]"
      />

      <div ref={ref} className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* ── Left: text ── */}
          <div className="flex-1 text-center lg:text-left space-y-5">
            <div className={inView ? 'animate-fade-in-up' : 'opacity-0'}>
              <h2
                className="font-display font-bold text-white leading-tight"
                style={{ fontSize: 'clamp(1.85rem, 4vw, 3rem)' }}
              >
                Join the <em className="text-gold-400 not-italic italic">H.E.R Community</em> and
                Stay Connected!
              </h2>
            </div>

            <div
              className={`w-12 h-[3px] rounded-full bg-gradient-to-r from-gold-400 to-crimson-500 mx-auto lg:mx-0 ${inView ? 'animate-fade-in' : 'opacity-0'}`}
              style={{ animationDelay: inView ? '0.15s' : undefined }}
            />

            <p
              className={`text-base-300 text-[15px] leading-[1.85] max-w-md mx-auto lg:mx-0 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: inView ? '0.2s' : undefined }}
            >
              Join our mailing list for resources, birth support tips, and updates. Be the first to
              learn about new offerings designed to support your healing, empowerment, and
              restoration.
            </p>

            <ul
              className={`space-y-2.5 inline-flex flex-col items-center lg:items-start ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: inView ? '0.3s' : undefined }}
            >
              {perks.map((perk) => (
                <li key={perk} className="flex items-center gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold-400 shrink-0" />
                  <span className="text-[13.5px] text-base-400">{perk}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Right: form ── */}
          <div
            className={`w-full lg:w-[420px] shrink-0 space-y-4 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{ animationDelay: inView ? '0.2s' : undefined }}
          >
            {submitted ? (
              <div className="bg-white/5 border border-white/10 rounded-2xl px-8 py-10 text-center space-y-3">
                <p className="font-display font-bold text-white text-xl">You're in!</p>
                <p className="text-base-400 text-sm leading-relaxed">
                  Welcome to the H.E.R community. Look out for our next update in your inbox.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name (Optional)"
                  disabled={loading}
                  className="w-full bg-crimson-950 border border-crimson-700 text-white placeholder:text-crimson-300/50 text-[14px] px-5 py-3.5 rounded-full focus:outline-none focus:ring-2 focus:ring-gold-400 transition-shadow disabled:opacity-60"
                />
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    required
                    disabled={loading}
                    className="flex-1 bg-crimson-950 border border-crimson-700 text-white placeholder:text-crimson-300/50 text-[14px] px-5 py-3.5 rounded-full focus:outline-none focus:ring-2 focus:ring-gold-400 transition-shadow disabled:opacity-60"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 disabled:opacity-70 text-base-900 font-semibold text-[14px] px-6 py-3.5 rounded-full transition-colors duration-200 whitespace-nowrap shrink-0"
                  >
                    {loading ? 'Joining…' : 'Sign Up Today!'}
                    {!loading && <RiArrowRightLine size={16} />}
                  </button>
                </div>
                {error && <p className="text-[12px] text-red-400 pl-2">{error}</p>}
                <p className="text-[11.5px] text-base-500 text-center sm:text-left pl-2">
                  No spam. Unsubscribe any time.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
